import { useMutation } from '@tanstack/react-query';

import { updateGameSwitchSession } from '@/api/game-switch/update-session';
import { getUpdateSessionEndpoint } from '@/api/helpers/get-update-session-endpoint';
import { useStore } from '@/store/useStore';
import { ddActionGameSwitchUpdateSession } from '@/utilities/logger/datadog/rumEvents/gameSwitchActions';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import fetchGameWindowData from '@/api/game-window-data/fetch-game-window-data';
import { setGameWindowContext } from '@/store/actions';
import applyGameWindowData from '@/api/game-window-data/apply-game-window-data';
import { usePopup } from '@/features/Popup/usePopup';
import { generatePopupConfig } from '@/features/Popup/helpers/generatePopupConfig';
import { ERROR_TYPE, POPUP_TYPES } from '@/features/Popup/constants';
import { computeLaunchJackpotMode } from '@/features/SWJ/helpers/jackpot-mode-launch';
import { clearGlsResponseJackpotMode } from '@/game-client/gls-jackpot-mode-tracker';
import { getFabricAuthToken } from '@/utilities/axios/get-fabric-auth-headers';

export type GameSwitchContext = {
  rgsCode?: string;
  provider?: string;
};

/**
 * Owns everything a game switch does: resolving userId/gameUid/token/jackpot
 * mode from the store, and the FGP request itself (retry, backoff, and the
 * RUM side effects tied to its outcome). Callers only decide *when* to
 * trigger a game switch (e.g. handleGameSwitch's experiment gate); this hook
 * is the seam for additional game-switch requests as they're added.
 */
export const useGameSwitch = () => {
  const { showPopup } = usePopup();

  const { mutateAsync: updateSession } = useMutation({
    mutationFn: updateGameSwitchSession,
    retry: 2,
    retryDelay: (attemptIndex: number) => (attemptIndex + 1) * 500,
    onSuccess: (_data, variables) => {
      const { region, brand } = useStore.getState().runtime;
      ddActionGameSwitchUpdateSession({
        region,
        brand: brand ?? '',
        updateSessionURL: getUpdateSessionEndpoint() ?? '',
        isJackpotModeActive: variables.jackpotMode,
      });
    },
    onError: (error, variables) => {
      Logger.logRumError({
        name: '[GW][GAME_SWITCH]: Update session failed',
        description: `Update session call failed. Err: ${error.message}`,
        useCase: DdEventCategory.GameSwitch,
        eventType: DdEventType.Error,
        context: { error, gameUid: variables.gameUid },
      });
    },
  });

  /** Resolves to the new game's uid on success; `undefined` when skipped or failed. */
  const gameSwitch = async ({
    rgsCode,
    provider,
  }: GameSwitchContext = {}): Promise<string | undefined> => {
    // Read the store once, at call time: a game switch fires asynchronously on
    // a GC/GAME_SWITCH event, so the freshest snapshot is the correct one (not
    // a render-time subscription). See the state-and-data skill's handler rule.
    const { userSessionContext, gameContext, runtime } = useStore.getState();
    const { userId } = userSessionContext;
    const { region, brand } = runtime;
    const state = `${brand?.startsWith('moh') ? 'ms' : 'fd'}-${region}`;

    // One guard for all required inputs: the combined condition keeps TS
    // narrowing (userId/gameUid become non-nullable below). A single RUM event
    // carries every input plus the list of missing ones, so dashboards can
    // facet on `context.missing` instead of needing per-field event names.
    const gameUid = gameContext.gameUid;

    if (!rgsCode || !userId || !gameUid) {
      const missing = [
        !rgsCode && 'rgsCode',
        !userId && 'userId',
        !gameUid && 'gameUid',
      ].filter(Boolean);

      Logger.logRumError({
        name: '[GW][GAME_SWITCH]: Missing required context',
        description: 'Game switch skipped: missing required context',
        useCase: DdEventCategory.GameSwitch,
        eventType: DdEventType.Error,
        context: { rgsCode, userId, gameUid, provider, missing },
      });
      return undefined;
    }

    setGameWindowContext({ gameSwitchInProgress: true });
    try {
      const token = await getFabricAuthToken('GAME_SWITCH');

      // step 1 - fetch the new game's window data. Region/product/platform are
      // resolved inside the fetcher from the runtime slice, so the caller only
      // supplies the provider's rgs code.
      const newGameData = await fetchGameWindowData({
        rgsDesktop: rgsCode,
      });

      if (newGameData?.game?.uid) {
        setGameWindowContext({ skipNextGlsReset: true });
        clearGlsResponseJackpotMode();
      }

      // step 2 - apply it so the store reflects the new game (gameUid, SWJ, etc.).
      applyGameWindowData(newGameData);

      // step 3 - reconcile the session with the now-current game
      await updateSession({
        userId,
        gameUid: useStore.getState().gameContext.gameUid ?? gameUid,
        jackpotMode: computeLaunchJackpotMode() ?? false,
        token,
      });

      return newGameData?.game?.uid;
    } catch (error) {
      Logger.logRumError({
        name: '[GW][GAME_SWITCH]: Game switch failed',
        description: `Failed gameSwitch function. Err: ${error}`,
        useCase: DdEventCategory.GameLaunch,
        eventType: DdEventType.Error,
        context: { rgsCode, state, provider },
      });

      showPopup(
        generatePopupConfig(POPUP_TYPES.PROVIDER, {
          errorConfig: { errorType: ERROR_TYPE.FAILED_TO_FETCH_GAME },
        }),
        POPUP_TYPES.PROVIDER,
      );
      return undefined;
    } finally {
      setGameWindowContext({ gameSwitchInProgress: false });
    }
  };

  return { gameSwitch };
};
